import {EditorSelection} from '@codemirror/state'
import type {EditorView} from '@codemirror/view'
import type {Literal, Nodes} from 'mdast'

import {isPositionedNode, type PositionedNode} from '../../../language-tools/syntax-tree/positioned-node'
import {getCurrentSelectionContext} from '../../extensions/selection-context'
import {createText} from '../../extensions/single-line-editor'
import {findWordBoundaries, hasNonEmptySelection} from '../../utils'
import {type FormatInlineNodeType, NODE_TYPE_TO_INLINE_DELIMITER_MAP} from './constants'

/**
 * Resolves delimiter configuration for a given format type
 * Returns an object with `prefix` and `suffix` delimiter strings
 */
export const resolveDelimiters = (type: FormatInlineNodeType) => {
  const delimiter = NODE_TYPE_TO_INLINE_DELIMITER_MAP[type]
  return typeof delimiter === 'string' ? {prefix: delimiter, suffix: delimiter} : delimiter
}

/**
 * Checks if the current selection has existing formatting of the specified type
 */
export const getExistingFormatting = (view: EditorView, type: FormatInlineNodeType): Nodes[] => {
  const selectionContext = getCurrentSelectionContext(view.state)
  return [...selectionContext].filter(node => node.type === type)
}

/**
 * Removes formatting delimiters around the current selection or cursor position.
 */
export const removeFormattingAtSelection = (view: EditorView, nodes: Nodes[]) => {
  const node = nodes[0] // Remove innermost wrapping first (if there are nested formats)
  if (!node?.children) return

  // Get all delimiter node positions to be removed
  const delimiterNodePositions = node.children
    .filter(child => child.type === 'delimiter' && isPositionedNode(child))
    .map(child => {
      const pNode = child as PositionedNode
      return {
        from: pNode.position.start.offset,
        to: pNode.position.end.offset,
      }
    })

  if (delimiterNodePositions.length === 0) return

  view.dispatch({
    changes: delimiterNodePositions,
    userEvent: 'input',
    scrollIntoView: true,
  })
}

/*
 * Put "<prefix>" before the selection range and "<suffix>" after the selection range
 */
export const wrapSelectionWithDelimiter = (
  view: EditorView,
  prefix: string,
  suffix: string,
  customRangePosition?: (from: number, to: number) => number,
) => {
  view.dispatch(
    view.state.changeByRange(range => {
      const defaultRange = EditorSelection.range(range.from + prefix.length, range.to + prefix.length)
      const finalRange = customRangePosition
        ? EditorSelection.cursor(customRangePosition(range.from, range.to))
        : defaultRange

      return {
        changes: [
          {from: range.from, insert: prefix},
          {from: range.to, insert: suffix},
        ],
        range: finalRange,
        userEvent: 'input',
        scrollIntoView: true,
      }
    }),
  )
}

/*
 * Inserts formatting delimiters around the word at the current cursor position.
 *
 * When no text is selected, this function places the prefix and suffix delimiters around
 * the word containing the cursor. For nested formatting (e.g., adding bold to text that's
 * already italic), it places the new delimiters inside the existing ones
 * by using word boundaries within the current formatting node.
 *
 * Example: cursor in `_wo|rd_` + bold formatting → `_**word**_`
 */
const insertDelimiterAtSelection = (view: EditorView, prefix: string, suffix: string, wordNode?: Literal) => {
  const caretLocation = view.state.selection.main.anchor

  // Determine text and offset for word boundary calculation
  const hasWordNode = wordNode?.value && isPositionedNode(wordNode)
  const {text, offset} = hasWordNode
    ? {text: wordNode.value, offset: caretLocation - wordNode.position.start.offset}
    : {text: view.state.doc.toString(), offset: caretLocation}
  const wordNodeStart = hasWordNode ? wordNode.position.start.offset : 0
  const {start: wordStart, end: wordEnd} = findWordBoundaries(text, offset)

  view.dispatch({
    changes: [
      {from: wordStart + wordNodeStart, insert: createText(prefix)},
      {from: wordEnd + wordNodeStart, insert: createText(suffix)},
    ],
    // Set new caret position inside the newly added delimiters because default CodeMirror behavior fails on complex insertions.
    selection: {anchor: caretLocation + prefix.length},
    userEvent: 'input',
    scrollIntoView: true,
  })
}

/**
 * Finds the relevant word node from the selection context
 */
const findWordNodeInContext = (selectionContext: Nodes[]) => {
  return selectionContext.find(node => node.type === 'text')
}

/**
 * Applies formatting to the current selection or cursor position
 */
export const applyFormatting = (
  view: EditorView,
  type: FormatInlineNodeType,
  customRangePosition?: (from: number, to: number) => number,
) => {
  const {prefix, suffix} = resolveDelimiters(type)
  const selectionContext = [...getCurrentSelectionContext(view.state)]

  if (hasNonEmptySelection(view.state)) {
    wrapSelectionWithDelimiter(view, prefix, suffix, customRangePosition)
  } else {
    const wordNode = findWordNodeInContext(selectionContext)
    insertDelimiterAtSelection(view, prefix, suffix, wordNode)
  }
}

/**
 * Generic formatter function that handles inline formatting types
 *
 * This function either removes existing formatting of the specified type
 * or applies new formatting to the current selection/cursor position.
 */
export const inlineFormatter = (view: EditorView, type: FormatInlineNodeType): boolean => {
  if (!view.hasFocus) view.focus()

  const existingFormattedNodes = getExistingFormatting(view, type)

  if (existingFormattedNodes.length > 0) {
    removeFormattingAtSelection(view, existingFormattedNodes)
  } else {
    applyFormatting(view, type)
  }

  return true
}
